import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Panel, Pill, Avatar, EmptyState } from "@/components/Bits";
import { toast } from "sonner";
import PhoneInput from "@/components/PhoneInput";
import {
  ArrowLeft, Phone, Envelope, MapPin, Heart, Warning, Bus, IdentificationCard,
  Plus, Trash, Star, Camera, ShieldCheck, User, UserCircle, Users,
} from "@phosphor-icons/react";

const ENROLMENT_LABELS = {
  preschool: { label: "Preschool", tone: "terracotta" },
  daycare: { label: "Daycare", tone: "sage" },
  preschool_daycare: { label: "Preschool + Daycare", tone: "mustard" },
  activity_only: { label: "Activity Only", tone: "lavender" },
  after_school: { label: "After School", tone: "lavender" },
};

const RELATIONSHIPS = ["father", "mother", "guardian", "grandfather", "grandmother", "uncle", "aunt", "sibling", "other"];

const ROLE_TONE = { father: "cobalt", mother: "lavender", guardian: "mustard" };
const ROLE_ICON = { father: User, mother: UserCircle, guardian: Users };

export default function StudentDetail() {
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [family, setFamily] = useState(null);
  const [showContactDialog, setShowContactDialog] = useState(false);

  const load = async () => {
    const r = await api.get(`/students/${studentId}`);
    setStudent(r.data);
    // Best-effort family fetch (admin-only on backend; family roles see own).
    api.get(`/students/${studentId}/family`)
      .then(f => setFamily(f.data))
      .catch(() => setFamily(null));
  };
  useEffect(() => { load(); }, [studentId]);

  const removeContact = async (cid) => {
    if (!window.confirm("Remove this contact?")) return;
    await api.delete(`/contacts/${cid}`);
    toast.success("Contact removed");
    load();
  };

  if (!student) return <div className="p-6 text-ink-soft">Loading student…</div>;
  const enrol = ENROLMENT_LABELS[student.enrolment_type] || ENROLMENT_LABELS.preschool;
  const contacts = student.contacts || [];

  return (
    <div data-testid="student-detail">
      <Link to="/app/students" className="inline-flex items-center gap-1 text-sm text-ink-soft mb-4" data-testid="back-to-students">
        <ArrowLeft size={16} /> Back to roster
      </Link>

      <PageHeader
        eyebrow="Student Profile"
        title={student.name}
        subtitle={`${student.age_group} · ${student.class_name}`}
        action={<Pill tone={enrol.tone}>{enrol.label}</Pill>}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Quick Info" testid="quick-info">
          <div className="flex items-center gap-3">
            <Avatar name={student.name} size={64} />
            <div>
              <div className="font-heading font-bold text-lg">{student.name}</div>
              <div className="text-xs text-ink-soft">{student.gender} · DOB {student.dob}</div>
              <div className="text-xs text-ink-soft mt-1">Blood: {student.blood_group}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <div className="bg-paper-alt rounded-xl p-3"><div className="text-ink-soft">Status</div><div className="font-semibold capitalize">{student.status}</div></div>
            <div className="bg-paper-alt rounded-xl p-3"><div className="text-ink-soft">Admitted</div><div className="font-semibold">{student.admission_date}</div></div>
          </div>
          {student.address && (
            <div className="mt-3 text-xs"><MapPin size={12} weight="duotone" className="inline mr-1 text-ink-soft" /> {student.address}</div>
          )}
        </Panel>

        <Panel title="Service Enrolment" testid="service-enrolment">
          <Pill tone={enrol.tone} className="text-sm">{enrol.label}</Pill>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center gap-2"><Bus size={16} weight="duotone" className="text-ink-soft" /> Transport: <span className="font-medium">{student.transport_required ? `Yes (${student.transport_route || "Pending route"})` : "No"}</span></div>
          </div>
        </Panel>

        <Panel title="Medical & Safety" testid="medical-safety">
          <div className="text-xs text-ink-soft">Blood: <span className="font-semibold text-ink">{student.blood_group || "—"}</span></div>
          <div className="mt-3">
            <div className="text-xs text-ink-soft mb-1">Allergies</div>
            <div className="flex flex-wrap gap-1">
              {(student.medical?.allergies || []).map(a => <Pill key={a} tone="danger"><Warning size={12} weight="bold" className="mr-1" /> {a}</Pill>)}
              {(student.medical?.allergies || []).length === 0 && <Pill tone="sage">None reported</Pill>}
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xs text-ink-soft mb-1">Conditions</div>
            <div className="flex flex-wrap gap-1">
              {(student.medical?.conditions || []).map(c => <Pill key={c} tone="mustard">{c}</Pill>)}
              {(student.medical?.conditions || []).length === 0 && <Pill tone="sage">None reported</Pill>}
            </div>
          </div>
        </Panel>
      </div>

      {family && (family.fathers?.length || family.mothers?.length || family.guardians?.length) ? (
        <Panel
          title="Family — Father · Mother · Guardian"
          className="mt-6"
          testid="family-details-panel"
          action={<Pill tone="sage">{(family.fathers?.length || 0) + (family.mothers?.length || 0) + (family.guardians?.length || 0)} on file</Pill>}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="family-cards">
            {["fathers", "mothers", "guardians"].flatMap(group =>
              (family[group] || []).map(p => (
                <FamilyMemberCard key={p.user_id} member={p} group={group.slice(0, -1)} />
              )),
            )}
          </div>
          {family.authorised_pickup?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-xs uppercase tracking-wider text-ink-soft font-semibold mb-2 flex items-center gap-1.5">
                <ShieldCheck size={12} weight="bold" /> Authorised pickup contacts
              </div>
              <div className="flex flex-wrap gap-2">
                {family.authorised_pickup.map(c => (
                  <div key={c.contact_id || c.name} className="bg-sage-soft/60 border border-sage-soft rounded-2xl px-3 py-2 text-xs">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-ink-soft">{c.relationship || "—"} · {c.phone || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      ) : null}

      <Panel
        title={`Family & Guardians (${contacts.length})`}
        className="mt-6"
        testid="contacts-panel"
        action={(
          <button data-testid="add-contact-btn" onClick={() => setShowContactDialog(true)} className="px-3 py-1.5 rounded-full bg-terracotta text-white text-xs font-semibold flex items-center gap-1">
            <Plus size={14} weight="bold" /> Add contact
          </button>
        )}
      >
        {contacts.length === 0 ? <EmptyState title="No contacts yet" /> : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="contacts-list">
            {contacts.map(c => (
              <div key={c.contact_id} className="border border-border rounded-2xl p-4 relative" data-testid={`contact-${c.contact_id}`}>
                <div className="flex items-start gap-3">
                  <Avatar name={c.name} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{c.name}</span>
                      {c.is_primary && <Pill tone="terracotta"><Star size={10} weight="fill" className="mr-1" /> Primary</Pill>}
                      {c.is_emergency && <Pill tone="mustard">Emergency</Pill>}
                    </div>
                    <div className="text-xs text-ink-soft mt-0.5 capitalize">{c.relationship} · {c.occupation || ""}</div>
                    {c.phone && <div className="text-xs mt-1"><Phone size={11} weight="duotone" className="inline mr-1" /> {c.phone}</div>}
                    {c.email && <div className="text-xs mt-0.5"><Envelope size={11} weight="duotone" className="inline mr-1" /> {c.email}</div>}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {c.can_pickup && <Pill tone="sage"><ShieldCheck size={10} weight="bold" className="mr-1" /> Pickup OK</Pill>}
                      {!c.can_pickup && <Pill tone="ink">Pickup not allowed</Pill>}
                    </div>
                  </div>
                  {!c.is_primary && (
                    <button onClick={() => removeContact(c.contact_id)} data-testid={`remove-${c.contact_id}`} className="w-8 h-8 rounded-xl hover:bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {showContactDialog && (
        <ContactDialog
          studentId={studentId}
          onClose={() => setShowContactDialog(false)}
          onCreated={() => { load(); setShowContactDialog(false); }}
        />
      )}
    </div>
  );
}

function FamilyMemberCard({ member, group }) {
  const Icon = ROLE_ICON[group] || User;
  const tone = ROLE_TONE[group] || "ink";
  return (
    <div className="bg-white border border-border rounded-2xl p-4 hover:shadow-hoverlift transition" data-testid={`family-${group}-${member.user_id}`}>
      <div className="flex items-start gap-3">
        {member.photo_url ? (
          <img src={member.photo_url} alt={member.name} className="w-12 h-12 rounded-2xl object-cover" />
        ) : (
          <div className={`w-12 h-12 rounded-2xl bg-${tone}-light flex items-center justify-center shrink-0`}>
            <Icon size={22} weight="duotone" className={`text-${tone}-deep`} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{member.name}</span>
            <Pill tone={tone}>{member.guardian_type || group}</Pill>
          </div>
          {member.relationship && member.relationship !== group && (
            <div className="text-[10px] text-ink-soft capitalize mt-0.5">Relationship: {member.relationship}</div>
          )}
          {member.phone && (
            <div className="text-xs mt-1.5 flex items-center gap-1 truncate">
              <Phone size={11} weight="duotone" className="text-ink-soft shrink-0" /> {member.phone}
            </div>
          )}
          {member.email && (
            <div className="text-xs mt-0.5 flex items-center gap-1 truncate">
              <Envelope size={11} weight="duotone" className="text-ink-soft shrink-0" /> {member.email}
            </div>
          )}
          {member.occupation && (
            <div className="text-xs mt-1 text-ink-soft">{member.occupation}{member.employer ? ` · ${member.employer}` : ""}</div>
          )}
          {member.emergency_contact && (
            <div className="mt-2 flex items-center gap-1 text-xs">
              <Warning size={11} weight="duotone" className="text-terracotta" />
              <span className="text-ink-soft">Emergency:</span>
              <span className="font-medium">{member.emergency_contact}</span>
            </div>
          )}
          {member.pickup_permission === true && (
            <div className="mt-2 inline-flex">
              <Pill tone="sage"><ShieldCheck size={10} weight="bold" className="mr-1" /> Pickup authorised</Pill>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactDialog({ studentId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("guardian");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [canPickup, setCanPickup] = useState(true);
  const [isEmergency, setIsEmergency] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name || !phone) return toast.error("Name & phone are required");
    setSaving(true);
    try {
      await api.post(`/students/${studentId}/contacts`, {
        name, relationship, phone, email: email || undefined,
        can_pickup: canPickup, is_emergency: isEmergency,
        notification_pref: ["whatsapp"],
      });
      toast.success("Contact added");
      onCreated();
    } catch (e) {
      toast.error("Failed to add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="contact-dialog">
        <div className="font-heading font-bold text-xl">Add Contact / Guardian</div>
        <p className="text-xs text-ink-soft mt-1">Multiple contacts can be added. Set pickup & emergency permissions per person.</p>
        <div className="mt-4 space-y-3">
          <input data-testid="contact-name" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" />
          <select data-testid="contact-relationship" value={relationship} onChange={e => setRelationship(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border">
            {RELATIONSHIPS.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
          </select>
          <PhoneInput value={phone} onChange={setPhone}
            placeholder="Mobile (with country code)" testId="contact-phone" />
          <input data-testid="contact-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)" className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" />
          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input data-testid="contact-can-pickup" type="checkbox" checked={canPickup} onChange={e => setCanPickup(e.target.checked)} />
              Authorised for pickup
            </label>
            <label className="flex items-center gap-2">
              <input data-testid="contact-is-emergency" type="checkbox" checked={isEmergency} onChange={e => setIsEmergency(e.target.checked)} />
              Emergency contact
            </label>
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-medium text-ink-soft">Cancel</button>
          <button data-testid="contact-submit" disabled={saving} onClick={submit} className="px-4 py-2 rounded-full bg-terracotta text-white text-sm font-semibold disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
